import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { UserActions } from '@recruitee/account-data';
import {
  InterviewScheduleLink,
  InterviewScheduleLinkReferences,
  InterviewTemplateReference,
  User,
  UserReference,
} from '@recruitee/http-types';
import { InterviewTemplateActions } from '@recruitee/interview-templates-data';
import { createEffectFactory } from '@recruitee/ngrx';

import { InterviewScheduleAssignmentsCollectionActions } from '../assignments-collection/assignments-collection.store';
import { InterviewSchedulesCollectionActions } from '../collection/collection.store';
import { InterviewScheduleResultRequestsCollectionActions } from '../result-requests-collection/result-requests-collection.store';
import { InterviewSchedulesLinkEntityHttpClient } from './link-entity.http-client';
import { InterviewSchedulesLinkEntityActions } from './link-entity.store';

@Injectable({ providedIn: 'root' })
export class InterviewSchedulesLinkEntityEffects {
  public effects = createEffectFactory(InterviewSchedulesLinkEntityActions, this.actions$);

  public fetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewSchedulesLinkEntityActions.fetch),
      switchMap(({ payload }) =>
        this.http.fetch(payload.id).pipe(
          switchMap(({ interviewSchedule, references }) => [
            ...this.getReferencesActions(interviewSchedule, references),
            InterviewSchedulesLinkEntityActions.fetchSuccess({
              payload: {
                ...interviewSchedule,
                interviewScheduleAssignmentsIds: interviewSchedule.interviewScheduleAssignments.map(
                  item => item.id!,
                ),
                interviewScheduleResultRequestsIds:
                  interviewSchedule.interviewScheduleResultRequests.map(item => item.id!),
              },
            }),
          ]),
          catchError(error =>
            of(
              InterviewSchedulesLinkEntityActions.fetchFailed({
                payload: error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewSchedulesLinkEntityActions.update),
      switchMap(({ payload }) =>
        this.http.update(payload).pipe(
          switchMap(({ interviewSchedule, references }) => [
            ...this.getReferencesActions(interviewSchedule, references),
            InterviewSchedulesLinkEntityActions.updateSuccess({
              payload: {
                ...interviewSchedule,
                interviewScheduleAssignmentsIds: interviewSchedule.interviewScheduleAssignments.map(
                  item => item.id!,
                ),
                interviewScheduleResultRequestsIds:
                  interviewSchedule.interviewScheduleResultRequests.map(item => item.id!),
              },
            }),
          ]),
          catchError(error =>
            of(
              InterviewSchedulesLinkEntityActions.updateFailed({
                payload: error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchDuplicateName$ = this.effects(
    InterviewSchedulesLinkEntityActions.fetchDuplicateName,
    ({ payload }) =>
      this.http.fetchDuplicateName(payload.id).pipe(
        map(payload =>
          InterviewSchedulesLinkEntityActions.fetchDuplicateNameSuccess({
            payload,
          }),
        ),
        catchError(error =>
          of(InterviewSchedulesLinkEntityActions.deleteFailed({ payload: error })),
        ),
      ),
  );

  public delete$ = this.effects(InterviewSchedulesLinkEntityActions.delete, ({ payload }) =>
    this.http.delete(payload.id).pipe(
      map(interviewScheduleLink =>
        InterviewSchedulesLinkEntityActions.deleteSuccess({
          payload: interviewScheduleLink,
        }),
      ),
      catchError(error => of(InterviewSchedulesLinkEntityActions.deleteFailed({ payload: error }))),
    ),
  );

  constructor(
    private actions$: Actions,
    private http: InterviewSchedulesLinkEntityHttpClient,
    private store: Store,
  ) {
    this.actions$
      .pipe(
        ofType(
          InterviewSchedulesLinkEntityActions.updateSuccess,
          InterviewSchedulesLinkEntityActions.deleteSuccess,
        ),
      )
      .subscribe(() => {
        this.store.dispatch(InterviewSchedulesCollectionActions.fetchCollection());
      });
  }

  private getReferencesActions(
    interviewSchedule: InterviewScheduleLink,
    references: InterviewScheduleLinkReferences[],
  ) {
    const users = <UserReference[]>references.filter(({ type }) => type === 'Admin');
    const interviewTemplates = <InterviewTemplateReference[]>(
      references.filter(({ type }) => type === 'InterviewTemplate')
    );

    return [
      ...(interviewTemplates.length
        ? [
            InterviewTemplateActions.references({
              payload: { data: interviewTemplates },
            }),
          ]
        : []),
      ...(users.length ? [UserActions.references({ payload: { data: <User[]>users } })] : []),
      InterviewScheduleAssignmentsCollectionActions.references({
        payload: interviewSchedule.interviewScheduleAssignments,
      }),
      InterviewScheduleResultRequestsCollectionActions.references({
        payload: interviewSchedule.interviewScheduleResultRequests,
      }),
    ];
  }
}
